import { useEffect, useId, useRef, type KeyboardEvent, type RefObject } from 'react';

/**
 * The keyboard half of a kebab menu: take the caret on open, walk the items
 * with the arrows, Home and End, and give the caret back to the trigger on
 * Escape or a pick.
 *
 * Written for the decks page's `DeckMenu` in Run 7 and lifted out here in Run
 * 11, when the Library's item menu needed exactly the same behaviour. It is a
 * hook rather than a component because the two menus' CONTENTS are their
 * pages' own contracts — eight deck verbs, six item verbs — and only the
 * keyboard is the same thing twice.
 *
 * What it exists to fix: Escape already CLOSED both menus (`useMenuDismiss`
 * listens at the document), but the element holding focus was unmounted with
 * the menu, so the caret landed on `<body>` and the next Tab restarted from the
 * top of the page. Closable is not the same as escapable.
 *
 * Two rules worth not undoing:
 *
 * - **The caret goes back only on a close that is still ours.** Clicking some
 *   other control closes this menu too, and dragging focus backwards out of
 *   whatever was just clicked is worse than losing it — so the return happens
 *   only when the active element is `<body>`.
 * - **Tab decides AFTER the browser has moved the caret.** Tabbing out of the
 *   menu is a dismissal, but a menu may hold something that is focusable and
 *   is not a `menuitem` (the Library's accent swatches are a radiogroup), and
 *   closing on the keydown would shut the menu on the way INTO it. So the close
 *   is deferred a frame and only runs if the caret really did leave.
 */

export type MenuKeys = {
  /** Put on the trigger, and named by the menu's `aria-labelledby`. */
  triggerId: string;
  btnRef: RefObject<HTMLButtonElement>;
  menuRef: RefObject<HTMLDivElement>;
  /** The menu's `onKeyDown`. */
  onMenuKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
};

export function useMenuKeys(menuOpen: boolean, close: () => void): MenuKeys {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerId = useId();
  // Was this menu open on the previous render? See the first rule above.
  const wasOpen = useRef(false);

  useEffect(() => {
    if (menuOpen) {
      wasOpen.current = true;
      menuRef.current?.querySelector<HTMLButtonElement>('[role=menuitem]:not(:disabled)')?.focus();
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) btnRef.current?.focus();
  }, [menuOpen]);

  function closeAndReturn() {
    close();
    btnRef.current?.focus();
  }

  // Roving by arrow, wrapping, over whatever items this menu actually has —
  // a deck's "Copy share link" only exists once it is published, so the list is
  // read from the DOM rather than counted by the caller.
  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const items = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]') ?? [])].filter(
      (el) => !el.disabled,
    );
    if (e.key === 'Escape') {
      // Handled here as well as at the document, because only this hook knows
      // where the caret should land afterwards.
      e.preventDefault();
      e.stopPropagation();
      closeAndReturn();
      return;
    }
    if (e.key === 'Tab') {
      const menu = menuRef.current;
      requestAnimationFrame(() => {
        if (menu && !menu.contains(document.activeElement)) close();
      });
      return;
    }
    if (!items.length) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      items[((at < 0 ? 0 : at) + delta + items.length) % items.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }

  return { triggerId, btnRef, menuRef, onMenuKeyDown };
}
